/**
 * Checklist Evidence Section Component
 *
 * Displays linked documents for a checklist item with options to:
 * - Upload new documents
 * - Link existing documents
 * - Find related documents with AI
 *
 * State machine for the Evidence section:
 *   empty (0 docs, no suggestions) -> show 3 action buttons
 *   isAiLoading -> show spinner
 *   hasSuggestions (suggestedDocuments.length > 0) -> show AISuggestionsPanel + buttons
 *   hasDocuments (documentIds.length > 0) -> show document cards + [+ Add Evidence]
 *
 * Note: hasSuggestions and hasDocuments can be true simultaneously.
 */

import { useState, useCallback, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import {
  FileSearch,
  FileText,
  Upload,
  Link2,
  Sparkles,
  Loader2,
  Plus,
  Unlink,
} from "lucide-react";
import type { UIChecklistItem, Id, DocumentId } from "@/packages/types";
import { useEvidenceDocuments } from "@/hooks/use-evidence-documents";
import { useChecklistActions } from "@/hooks/use-checklist-actions";
import { useLinkDocument } from "@/hooks/use-link-document";
import { useUnlinkDocument } from "@/hooks/use-unlink-document";
import { useAudits } from "@/hooks/use-audits";
import { Button } from "@/components/ui";
import { Dropdown } from "@/components/ui/Dropdown";
import { useTranslation } from "@/packages/utils";
import { UploadSheet } from "@/components/documents/upload/UploadSheet";
import { LinkDocumentDialog } from "./LinkDocumentDialog";
import { AISuggestionsPanel, AINoMatchesMessage } from "./AISuggestionsPanel";

// ─── Tag Pill ─────────────────────────────────────────────────────────────────

function TagPill({ name, color }: { name: string; color: string }) {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium"
      style={{
        borderColor: `${color}40`,
        color,
        backgroundColor: `${color}14`,
      }}
    >
      <span
        className="size-1.5 rounded-full shrink-0"
        style={{ backgroundColor: color }}
      />
      {name}
    </span>
  );
}

// ─── Skeleton Card ────────────────────────────────────────────────────────────

function SkeletonCard({ delay }: { delay: number }) {
  return (
    <div
      className="flex items-center gap-3 rounded-lg border border-border bg-card px-3 py-2.5 animate-pulse"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="rounded-md bg-muted/30 size-7 shrink-0" />
      <div className="flex-1 space-y-1.5">
        <div className="rounded bg-muted/30 h-3 w-2/3" />
        <div className="rounded bg-muted/20 h-2.5 w-1/2" />
      </div>
    </div>
  );
}

// ─── Empty State with Actions ─────────────────────────────────────────────────

interface EmptyStateProps {
  onUpload: () => void;
  onLinkExisting: () => void;
  onFindWithAI: () => void;
  isAiLoading: boolean;
}

function EmptyStateWithActions({
  onUpload,
  onLinkExisting,
  onFindWithAI,
  isAiLoading,
}: EmptyStateProps) {
  const { t } = useTranslation("audits");

  if (isAiLoading) {
    return (
      <div className="flex flex-col items-center text-center py-8 gap-2">
        <Loader2 size={28} className="text-accent animate-spin" />
        <p className="text-[13px] font-medium text-secondary">
          {t("checklist.evidence.findingMatchingDocuments")}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center text-center py-6 gap-3">
      <FileSearch size={32} className="text-muted/40" />
      <p className="text-[13px] font-medium text-secondary">
        {t("checklist.evidence.noDocumentsLinked")}
      </p>
      <p className="text-[12px] text-muted mb-2">
        {t("checklist.evidence.linkEvidenceDescription")}
      </p>
      <div className="flex flex-wrap justify-center gap-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={onUpload}
          className="gap-1.5"
        >
          <Upload size={14} />
          {t("checklist.evidence.upload")}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={onLinkExisting}
          className="gap-1.5"
        >
          <Link2 size={14} />
          {t("checklist.evidence.linkExisting")}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={onFindWithAI}
          className="gap-1.5"
        >
          <Sparkles size={14} />
          {t("checklist.evidence.findWithAI")}
        </Button>
      </div>
    </div>
  );
}

// ─── Document Card ────────────────────────────────────────────────────────────

function formatDate(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

interface DocCardProps {
  id: DocumentId;
  fileName: string;
  uploadedAt: number;
  uploaderName: string;
  tags: { name: string; color: string }[];
  categoryName: string | null;
  index: number;
  onOpen: (id: DocumentId) => void;
  onUnlink?: (id: DocumentId) => void;
}

function DocCard({
  id,
  fileName,
  uploadedAt,
  uploaderName,
  tags,
  categoryName,
  index,
  onOpen,
  onUnlink,
}: DocCardProps) {
  return (
    <motion.div
      className="w-full flex items-center gap-3 rounded-lg border border-border bg-card px-3 py-2.5 hover:bg-accent/5 hover:border-accent/30 transition-colors duration-150 group"
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.04, duration: 0.15 }}
    >
      <button
        type="button"
        className="flex items-center gap-3 flex-1 min-w-0 text-left cursor-pointer"
        onClick={() => onOpen(id)}
      >
        <div className="rounded-md bg-accent/10 p-1.5 shrink-0">
          <FileText size={16} className="text-accent" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-medium text-primary truncate">
            {fileName}
          </p>
          <p className="text-[11px] text-muted">
            {formatDate(uploadedAt)} · {uploaderName}
          </p>
        </div>
      </button>
      {(categoryName || tags.length > 0) && (
        <div className="shrink-0 flex items-center gap-1">
          {categoryName && (
            <span className="text-[11px] text-muted bg-muted/10 rounded-full px-2 py-0.5">
              {categoryName}
            </span>
          )}
          {tags.map((tag) => (
            <TagPill key={tag.name} name={tag.name} color={tag.color} />
          ))}
        </div>
      )}
      {onUnlink && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onUnlink(id);
          }}
          className="shrink-0 p-1 rounded hover:bg-red-500/10 text-muted hover:text-red-400 transition-colors cursor-pointer opacity-0 group-hover:opacity-100"
          title="Unlink document"
        >
          <Unlink size={14} />
        </button>
      )}
    </motion.div>
  );
}

// ─── Main Export ──────────────────────────────────────────────────────────────

interface EvidenceContentProps {
  item: UIChecklistItem;
  auditId: Id<"audits">;
  onOpenDocument?: (docId: DocumentId) => void;
}

export function EvidenceContent({
  item,
  auditId,
  onOpenDocument,
}: EvidenceContentProps) {
  const { t } = useTranslation("audits");
  const docIds = item.linkedDocumentIds ?? [];
  const { documents, isLoading } = useEvidenceDocuments(docIds);
  const { audits } = useAudits();
  const audit = audits?.find((a) => a.id === auditId);
  const preSelectedAudit = audit
    ? { id: auditId, displayName: audit.displayName, code: audit.code }
    : undefined;

  // Get checklist actions for AI find related documents
  const checklistActions = useChecklistActions(auditId);

  // Link document hook for accepting AI suggestions
  const linkDocument = useLinkDocument();

  // Unlink document hook
  const unlinkDocument = useUnlinkDocument();

  // Dialog/sheet state
  const [uploadOpen, setUploadOpen] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [isAiLoading, setIsAiLoading] = useState(false);

  // Track whether user has triggered AI search (to detect "no results" vs "not run yet")
  const [hasTriggeredAi, setHasTriggeredAi] = useState(false);

  // Track suggestion count at the time AI was triggered, to detect "no new results"
  const suggestionsCountBeforeAi = useRef<number>(0);

  // Derive AI-related state from the reactive item data
  const suggestions = item.suggestedDocuments ?? [];
  const hasSuggestions = suggestions.length > 0;
  const suggestedDocumentsStatus = item.suggestedDocumentsStatus;

  // Detect AI processing state from the backend status field (reactive via Convex)
  const isAiProcessing =
    suggestedDocumentsStatus === "pending" ||
    suggestedDocumentsStatus === "generating";

  // Show loading when either local state or backend status indicates processing
  const showAiLoading = isAiLoading || isAiProcessing;

  // Detect "no matches found" after AI completed: user triggered AI, it finished,
  // and suggestion count hasn't increased
  const showNoMatches =
    hasTriggeredAi &&
    !showAiLoading &&
    suggestedDocumentsStatus === "completed" &&
    suggestions.length <= suggestionsCountBeforeAi.current &&
    !hasSuggestions;

  // When backend status transitions away from processing, clear local loading
  useEffect(() => {
    if (!isAiProcessing && isAiLoading) {
      setIsAiLoading(false);
    }
  }, [isAiProcessing, isAiLoading]);

  // Handle AI find related documents
  const handleFindWithAI = useCallback(async () => {
    setIsAiLoading(true);
    setHasTriggeredAi(true);
    suggestionsCountBeforeAi.current = suggestions.length;

    try {
      await checklistActions.findRelatedDocuments(item.id);
      // Mutation enqueues a workpool job - results stored on checklistItem.suggestedDocuments
      // The loading state will be cleared when suggestedDocumentsStatus changes reactively
    } catch {
      // Error handled by the mutation wrapper (toast)
      setIsAiLoading(false);
    }
  }, [checklistActions, item.id, suggestions.length]);

  // Handle unlinking a document from the checklist item
  const handleUnlinkDocument = useCallback(
    async (documentId: DocumentId) => {
      await unlinkDocument({
        documentId,
        checklistItemId: item.id,
      });
    },
    [unlinkDocument, item.id],
  );

  // Handle accepting an AI suggestion (links the document to the checklist item)
  const handleAcceptSuggestion = useCallback(
    async (documentId: Id<"documents">) => {
      await linkDocument({
        documentId,
        checklistItemId: item.id,
      });
    },
    [linkDocument, item.id],
  );

  if (isLoading) {
    return (
      <div className="space-y-2">
        <SkeletonCard delay={0} />
        <SkeletonCard delay={80} />
        <SkeletonCard delay={160} />
      </div>
    );
  }

  const hasDocuments = documents.length > 0;

  return (
    <>
      {/* Add Evidence header button — visible when documents OR suggestions exist */}
      {(hasDocuments || hasSuggestions) && (
        <div className="flex justify-end mb-2">
          <Dropdown>
            <Dropdown.Trigger>
              <Button variant="secondary" size="sm" className="gap-1">
                <Plus size={14} />
                {t("checklist.evidence.addEvidence")}
              </Button>
            </Dropdown.Trigger>
            <Dropdown.Content>
              <Dropdown.Item
                icon={<Upload size={14} />}
                onSelect={() => setUploadOpen(true)}
              >
                {t("checklist.evidence.uploadDocument")}
              </Dropdown.Item>
              <Dropdown.Item
                icon={<Link2 size={14} />}
                onSelect={() => setLinkDialogOpen(true)}
              >
                {t("checklist.evidence.linkExisting")}
              </Dropdown.Item>
              <Dropdown.Item
                icon={<Sparkles size={14} />}
                onSelect={handleFindWithAI}
              >
                {t("checklist.evidence.findWithAI")}
              </Dropdown.Item>
            </Dropdown.Content>
          </Dropdown>
        </div>
      )}

      {/* AI loading spinner */}
      {showAiLoading && (
        <div className="flex flex-col items-center text-center py-6 gap-2 mb-3">
          <Loader2 size={28} className="text-accent animate-spin" />
          <p className="text-[13px] font-medium text-secondary">
            {t("checklist.evidence.findingMatchingDocuments")}
          </p>
        </div>
      )}

      {/* AI no matches message */}
      {showNoMatches && !showAiLoading && <AINoMatchesMessage />}

      {/* AI Suggestions Panel — displayed alongside documents */}
      {hasSuggestions && !showAiLoading && (
        <div className="mb-3">
          <AISuggestionsPanel
            checklistItemId={item.id}
            suggestions={suggestions}
            linkedDocumentIds={docIds}
            onAccept={handleAcceptSuggestion}
          />
        </div>
      )}

      {/* Empty state with action buttons — only when no docs, no suggestions, no loading */}
      {!hasDocuments && !hasSuggestions && !showAiLoading && !showNoMatches && (
        <EmptyStateWithActions
          onUpload={() => setUploadOpen(true)}
          onLinkExisting={() => setLinkDialogOpen(true)}
          onFindWithAI={handleFindWithAI}
          isAiLoading={false}
        />
      )}

      {/* Document cards */}
      {hasDocuments && (
        <motion.div
          className="space-y-2"
          initial="hidden"
          animate="visible"
          variants={{ visible: { transition: { staggerChildren: 0.04 } } }}
        >
          {documents.map((doc, i) => (
            <DocCard
              key={doc.id}
              id={doc.id}
              fileName={doc.fileName}
              uploadedAt={doc.uploadedAt}
              uploaderName={doc.uploaderName}
              tags={doc.tags}
              categoryName={doc.categoryName}
              index={i}
              onOpen={onOpenDocument ?? (() => {})}
              onUnlink={handleUnlinkDocument}
            />
          ))}
        </motion.div>
      )}

      {/* Action buttons below suggestions/docs when both exist or only suggestions */}
      {!hasDocuments && (hasSuggestions || showNoMatches) && !showAiLoading && (
        <div className="flex flex-wrap justify-center gap-2 pt-3">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setUploadOpen(true)}
            className="gap-1.5"
          >
            <Upload size={14} />
            {t("checklist.evidence.upload")}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setLinkDialogOpen(true)}
            className="gap-1.5"
          >
            <Link2 size={14} />
            {t("checklist.evidence.linkExisting")}
          </Button>
        </div>
      )}

      {/* Upload Sheet */}
      <UploadSheet
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        preSelectedAudit={preSelectedAudit}
      />

      {/* Link Document Dialog */}
      <LinkDocumentDialog
        open={linkDialogOpen}
        onOpenChange={setLinkDialogOpen}
        checklistItemId={item.id}
        auditId={auditId}
      />
    </>
  );
}
